import axios from 'axios';
import { toast } from 'sonner';
import i18n from '../locales/i18n';
import { seatSelfHealThrottle } from './seatSelfHealThrottle';
import { isBackgroundRequest, markServerTouch } from './sessionActivity';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5148',
  withCredentials: true,
  timeout: 30000,
});

let isRedirectingToLogin = false;

const EMPTY_RESPONSE = { data: { status: false, data: null } };

// Blob.text() eski tarayıcılarda/jsdom'da yok — FileReader yedeği.
const readBlobText = (blob) => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

// Silently re-issue the session cookie with freshly recomputed privileges.
// Deduped so a burst of 403s triggers exactly one refresh. On success it
// broadcasts the fresh user detail so AuthContext updates the menu/privileges
// live (no reload). Returns true when the session was refreshed.
let refreshInFlight = null;
function refreshSessionOnce() {
  if (!refreshInFlight) {
    refreshInFlight = api
      .post('/v1/auth/refresh-session', {}, { _skipSelfHeal: true, skipErrorToast: true })
      .then((res) => {
        const detail = res?.data?.data;
        if (detail) window.dispatchEvent(new CustomEvent('auth:refreshed', { detail }));
        return Boolean(detail);
      })
      .catch(() => false)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

// Oturumun 401 nedenleri (backend): hareketsizlik penceresi doldu / MFA gerekiyor. İkisi de
// "oturum düştü" demektir ama kullanıcıya NEDENİ söylenir.
const SESSION_END_MESSAGES = ['auth.sessionIdleTimeout', 'auth.mfaRequired'];

// Arka plan OLMAYAN her kimlikli yanıt sunucudaki hareketsizlik penceresini uzatmıştır
// (AuthenticationSetup) — canlı tutma pingi buna bakar (sessionActivity).
const noteServerTouch = (config, status) => {
  if (status === 401 || isBackgroundRequest(config)) return;
  markServerTouch();
};

api.interceptors.response.use(
  async (response) => {
    noteServerTouch(response.config, response.status);
    // AKM POC §7.1 — dışa aktarma istekleri responseType: 'blob' ister; eşiği aşan rapor 202 + JSON döner ve gövde de
    // Blob gelir. JSON türlü Blob çözülür ki kuyruk mesajı (reportExport.queued) toast'a, iş bilgisi çağırana ulaşsın.
    // Dosya (xlsx/pdf/zip) Blob'larına dokunulmaz.
    if (typeof Blob !== 'undefined' && response.data instanceof Blob && /json/i.test(response.data.type || '')) {
      try {
        response.data = JSON.parse(await readBlobText(response.data));
      } catch {
        // gövde JSON değil — olduğu gibi bırakılır
      }
    }
    const message = response.data?.message;
    if (message && ['post', 'put', 'patch', 'delete'].includes(response.config.method)) {
      const translated = i18n.t(`responseErrors.${message}`, {
        defaultValue: message,
        ...(response.data?.messageParams || {}),
      });
      toast.success(translated);
    }
    return response;
  },
  async (error) => {
    const method = error.config?.method;
    const isMutation = ['post', 'put', 'patch', 'delete'].includes(method);

    if (!error.response) {
      toast.error(i18n.t('common.connectionError', { defaultValue: 'Sunucuya bağlanılamadı.' }));
      return isMutation ? Promise.reject(error) : Promise.resolve(EMPTY_RESPONSE);
    }

    // responseType: 'blob' isteklerinde (CSV indirme) hata gövdesi de Blob gelir — JSON ise çözülür ki mesaj anahtarı ve
    // messageParams toast'a ulaşsın (final inceleme I5). Çözülemezse gövde olduğu gibi kalır.
    if (typeof Blob !== 'undefined' && error.response.data instanceof Blob && /json/i.test(error.response.data.type || '')) {
      try {
        error.response.data = JSON.parse(await readBlobText(error.response.data));
      } catch {
        // gövde JSON değil — genel hata toast'u
      }
    }

    const status = error.response.status;
    noteServerTouch(error.config, status);
    const url = error.config?.url || '';
    const isAuthLogin = url.includes('/auth/login');
    const isAuthMe = url.includes('/auth/me');
    const isAuthUrl = url.includes('/auth/');

    // 403 self-heal: a permission granted mid-session (e.g. ATS AT.Read when the
    // user is assigned as Hiring Manager) is not in the live JWT cookie until it
    // is re-issued — which used to require a logout/login. Silently refresh the
    // session once and retry the original request. Guarded against loops: the
    // refresh call is flagged _skipSelfHeal, and each request self-heals at most
    // once. If the retry still 403s, the error surfaces normally.
    //
    // Modül Bazlı Lisanslama — Ruling 67 (Ruling 64'ün düzeltmesi):
    //  - `license.seatUnavailable` (RequireModuleSeat: bu kişinin bu modülde koltuğu yok) self-heal'i
    //    ÇALIŞTIRIR ama MODÜL BAŞINA 10 DAKİKADA EN FAZLA BİR KEZ (seatSelfHealThrottle; modül 403
    //    gövdesinin `data.module` alanından). Web'de token'ı başka yenileyen yok: koltuk oturum
    //    ortasında verilince ya da limit kalkınca kurtuluş yolu budur. Sayaç koşulun EN SONUNDA
    //    sorulur — yalnız onarım gerçekten çalışacaksa an kaydedilir.
    //  - `license.seatPoolFull` (yönetici BAŞKASINA koltuk açamadı — Ruling 68) onarılmaz: token'ı
    //    yenilemek havuzu boşaltmaz.
    const seatMessage = error.response?.data?.message;
    const isSeatDenial = seatMessage === 'license.seatUnavailable';
    const isSeatPoolFull = seatMessage === 'license.seatPoolFull';
    if (status === 403 && !isAuthUrl && !isSeatPoolFull && error.config && !error.config._selfHealed && !error.config._skipSelfHeal
      && (!isSeatDenial || seatSelfHealThrottle.tryAcquire(error.response?.data?.data?.module))) {
      error.config._selfHealed = true;
      const refreshed = await refreshSessionOnce();
      if (refreshed) {
        return api(error.config);
      }
    }

    if (status === 401) {
      if (isAuthLogin) {
        const backendMessage = error.response?.data?.message;
        if (backendMessage) {
          const translated = i18n.t(`responseErrors.${backendMessage}`, { defaultValue: backendMessage });
          toast.error(translated);
        }
        return Promise.reject(error);
      }

      // /me: AuthContext karar verir (giriş ekranı). skipAuthRedirect: çağıran oturumun zaten
      // bittiğini biliyor (hareketsizlik çıkışının kendi logout çağrısı) — ikinci toast/yönlendirme yok.
      if (isAuthMe || error.config?.skipAuthRedirect) {
        return Promise.reject(error);
      }

      if (!isRedirectingToLogin) {
        isRedirectingToLogin = true;
        const endMessage = error.response?.data?.message;
        const reason = SESSION_END_MESSAGES.includes(endMessage) ? endMessage : 'auth.sessionExpired';
        toast.error(i18n.t(`responseErrors.${reason}`));
        window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason } }));
        setTimeout(() => { isRedirectingToLogin = false; }, 3000);
      }
      return Promise.resolve(EMPTY_RESPONSE);
    }

    // Show error toast — unless the caller opted out (e.g. an endpoint that
    // legitimately 404s as an empty state, like an exit interview that hasn't
    // been created yet). Mirrors the mobile `skipErrorToast` flag.
    //
    // `skipErrorToast` accepts two shapes:
    //   - `true`  — suppress unconditionally, for every outcome of this request
    //     (existing behaviour, unchanged; e.g. talent-reports/config/index.js's
    //     flight-risk fetch, where the component handles every failure itself).
    //   - an array of responseError message keys — suppress ONLY when the
    //     response's `message` is one of those keys; anything else (a different
    //     error, including a different 409 sub-code from the same endpoint)
    //     toasts normally. This is for a caller that special-cases exactly one
    //     known outcome (e.g. opens its own dialog) but wants every other
    //     outcome — including ones it doesn't know how to name yet — to fall
    //     through to the default toast, per the "no catch-and-toast in
    //     components" rule.
    const skipErrorToast = error.config?.skipErrorToast;
    const message = error.response?.data?.message;
    const shouldSkipToast = skipErrorToast === true
      || (Array.isArray(skipErrorToast) && !!message && skipErrorToast.includes(message));
    if (!shouldSkipToast) {
      if (message) {
        // `messageParams` (backend ResponseObjectModel) i18n interpolasyonunu besler —
        // "3 aktif pozisyonda kullanılıyor" gibi sayılı mesajlar tek anahtarla kurulur.
        // Yalnız bu alan geçilir; yanıtın `data` yükü ASLA i18n seçeneklerine sızdırılmaz.
        const translated = i18n.t(`responseErrors.${message}`, {
          defaultValue: message,
          ...(error.response?.data?.messageParams || {}),
        });
        toast.error(translated);
      } else {
        toast.error(i18n.t('common.error', { defaultValue: 'Bir hata oluştu.' }));
      }
    }

    // Mutations → reject (callers use try/catch, will handle it)
    // Reads → resolve empty (callers use .then/.finally, won't crash).
    // Opt-in `passErrorBody: true` (request config): resolve with the REAL
    // backend error body ({ status:false, message, ... }) instead of
    // EMPTY_RESPONSE, so the caller can distinguish a specific named outcome
    // (e.g. the 409 performance.feedback.teamViewDisabled flag gate) from
    // plain "no data". Only affects requests that set the flag — the default
    // EMPTY_RESPONSE behaviour for every other GET is unchanged.
    if (isMutation) return Promise.reject(error);
    if (error.config?.passErrorBody && error.response?.data) {
      return Promise.resolve({ data: error.response.data });
    }
    return Promise.resolve(EMPTY_RESPONSE);
  }
);

export default api;
